// Usage counting for the site: page views and the booking funnel.
//
// Two layers, both optional at runtime:
//   1. First-party, cookieless, always on in production. Each event is one
//      small POST to /api/track (netlify/functions/track.js) that stores the
//      event name, the page path, and a few non-identifying props (nights,
//      failure reason). No cookies, no IP address, no user agent, no IDs.
//      Read back on /admin under "Funnel".
//   2. Google Analytics 4, only when VITE_GA_MEASUREMENT_ID is set at build
//      time. Then gtag.js loads with IP anonymization and every event here is
//      mirrored to it. The privacy page reads `analyticsProviders` so it
//      describes whichever is live.
//
// Why first-party first: the site promised no analytics cookies, the owner
// could not see whether a human had ever asked for a quote, and no GA
// property existed. Counting our own funnel needs neither a consent banner
// nor an account, and it is the only way the numbers exist from today.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

export const analyticsProviders = Object.freeze({ firstParty: true, ga: Boolean(GA_ID) });

function enabled() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (navigator.webdriver) return false; // headless browsers and prerender
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return false;
  return true;
}

// Route params that would otherwise make one page look like many.
// The trailing slash is stripped FIRST. The other order anchored the archive
// pattern at $ against a path that might still end in one, so
// /archive/2026-09-19/ would have counted as its own page instead of folding
// into /archive/:date. Not reachable through the app's own links today, but a
// shared or rewritten URL is not the app's to control.
export function normalizePath(pathname) {
  const trimmed = String(pathname || '/').replace(/\/+$/, '') || '/';
  return trimmed.replace(/^\/archive\/\d{4}-\d{2}-\d{2}$/, '/archive/:date');
}

export function track(event, props = {}) {
  if (!enabled()) return;
  const path = normalizePath(window.location.pathname);
  const payload = JSON.stringify({ event, props: { ...props, path } });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/track', new Blob([payload], { type: 'application/json' }));
    } else {
      fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(() => {});
    }
  } catch {
    // Counting must never break the page.
  }
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('event', event, { ...props, page_path: path });
    } catch {
      // same
    }
  }
}

export function trackPageView(pathname) {
  track('page_view', { path: normalizePath(pathname) });
}

// Loads gtag.js once when a measurement ID is configured. Page views are sent
// by <PageView />, not by gtag's own config call, so SPA navigations count.
export function initAnalytics() {
  if (!GA_ID || !enabled() || window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);
}
